import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Alert, Table } from 'react-bootstrap';
import Select from 'react-select';
import { Save, RefreshCw, Zap } from 'lucide-react';
import Swal from 'sweetalert2';
import * as payrollApi from '../../../../services/api/payrollApi';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import * as S from '../../Common/internalToolsStyles';

/**
 * VacationBalanceSection — payroll-admin form for setting each employee's
 * semester balance (Semesterlagen Layer 2). Rendered as a section inside
 * the unified `SemesterPage`.
 *
 * Backs:
 *   GET  /payroll/vacation-balance?employeeId=...
 *   PUT  /payroll/vacation-balance
 *
 * Layer 2 model: earned/carried/saved are admin-set (no auto-accrual),
 * used is derived from approved SEMESTER time_entry rows. This section
 * only edits the three admin-set fields.
 */
const VacationBalanceSection = () => {
  const { companyId } = useParams();

  const currentYear = (() => {
    const now = new Date();
    // Semesterår April-March default. Year of April 1 start.
    return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  })();

  const [year, setYear] = useState(currentYear);
  const [employees, setEmployees] = useState([]);
  const [snapshots, setSnapshots] = useState({}); // employeeId → snapshot
  const [drafts, setDrafts] = useState({});       // employeeId → { earnedDays, carriedOverDays, savedDays }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});        // employeeId → bool
  const [accruing, setAccruing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch active employees via the existing hr/employees endpoint.
      // No dedicated personellApi module exists — the pattern in
      // EmployeeList calls this URL directly.
      const empRes = await axios.get(getApiUrl(`${companyId}/hr/employees`), { withCredentials: true });
      const emps = empRes.data?.activeEmployees || empRes.data?.employees || [];
      setEmployees(emps);

      // Fetch snapshots for each employee in parallel
      const snapEntries = await Promise.all(
        emps.map(async (e) => {
          try {
            const r = await payrollApi.getVacationBalance(companyId, { employeeId: e.employee_id });
            return [e.employee_id, r.data?.snapshot || null];
          } catch {
            return [e.employee_id, null];
          }
        }),
      );
      const snapMap = Object.fromEntries(snapEntries);
      setSnapshots(snapMap);

      // Seed drafts from current snapshot values so typing replaces
      // them cleanly.
      const draftMap = {};
      for (const [empId, snap] of snapEntries) {
        if (snap) {
          draftMap[empId] = {
            earnedDays: String(snap.earnedDays || 0),
            carriedOverDays: String(snap.carriedOverDays || 0),
            savedDays: String(snap.savedDays || 0),
          };
        }
      }
      setDrafts(draftMap);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda semesterdata');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const setDraftField = (empId, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [empId]: { ...(prev[empId] || {}), [field]: value },
    }));
  };

  const handleAccrueAll = async () => {
    const confirm = await Swal.fire({
      title: `Kör §7-intjäning för semesterår ${year}/${year + 1}?`,
      html: (
        'Beräknar tjänade dagar (prorata efter anställningstid under intjänandeåret). '
        + 'Uppdaterar alla aktiva anställda. Manuellt satta värden skrivs <strong>inte</strong> över '
        + 'om force-flaggan är avstängd.'
      ),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Kör intjäning',
      cancelButtonText: 'Avbryt',
    });
    if (!confirm.isConfirmed) return;

    setAccruing(true);
    setError(null);
    try {
      const res = await payrollApi.accrueVacationBalance(companyId, { year, force: false });
      const data = res.data?.data || res.data;
      const count = data?.updated?.length ?? data?.count ?? 0;
      setSuccess(`Intjäning klar. ${count} anställda uppdaterade.`);
      setTimeout(() => setSuccess(null), 5000);
      await loadData();
    } catch (err) {
      // §18 cap violation comes back as 409 / 422 with a structured message.
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      await Swal.fire({
        title: 'Intjäning blockerad',
        text: msg || 'Något gick fel vid intjäning.',
        icon: 'error',
      });
    } finally {
      setAccruing(false);
    }
  };

  const handleSave = async (empId) => {
    setSaving(prev => ({ ...prev, [empId]: true }));
    setError(null);
    try {
      const d = drafts[empId] || {};
      const payload = {
        employeeId: empId,
        year,
        earnedDays: Number(d.earnedDays) || 0,
        carriedOverDays: Number(d.carriedOverDays) || 0,
        savedDays: Number(d.savedDays) || 0,
      };
      await payrollApi.setVacationBalance(companyId, payload);
      // Refresh just this employee's snapshot
      const r = await payrollApi.getVacationBalance(companyId, { employeeId: empId });
      setSnapshots(prev => ({ ...prev, [empId]: r.data?.snapshot || null }));
      setSuccess('Semestersaldo sparat');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte spara semestersaldo');
    } finally {
      setSaving(prev => ({ ...prev, [empId]: false }));
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spinner animation="border" /></div>;
  }

  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 3 + i)
    .map(y => ({ value: y, label: `Semesterår ${y}/${y + 1} (1 apr ${y} – 31 mar ${y + 1})` }));

  return (
    <>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <div style={{ ...S.card, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <label style={{ fontSize: 11, color: S.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>
              Semesterår
            </label>
            <Select
              options={yearOptions}
              value={yearOptions.find(o => o.value === year)}
              onChange={(opt) => setYear(opt.value)}
              isSearchable={false}
              styles={{ control: b => ({ ...b, minHeight: 36, fontSize: 13 }) }}
            />
          </div>
          <button onClick={loadData} style={{ ...S.btnSecondary }} disabled={loading}>
            <RefreshCw size={14} /> Uppdatera
          </button>
          <button
            onClick={handleAccrueAll}
            style={{ ...S.btnPrimary }}
            disabled={accruing || loading}
            title="Kör Semesterlagen §7 prorata-intjäning för alla aktiva anställda"
          >
            <Zap size={14} /> {accruing ? 'Intjänar…' : 'Intjäna §7 (alla)'}
          </button>
        </div>

        <div style={{ fontSize: 12, color: S.colors.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
          Layer 2-läge: <strong>Tjänat</strong>, <strong>Överförda</strong> och <strong>Sparat</strong> sätts
          manuellt av lönehandläggare. <strong>Använd</strong> beräknas automatiskt från godkända SEMESTER-
          poster. <strong>Kvar</strong> = max(0, Tjänat + Överförda − Använd). Förskott uppstår när Använd
          överstiger Tjänat + Överförda — tillåts men visas tydligt.
        </div>

        <Table responsive borderless style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th style={S.tableHeaderCell}>Anställd</th>
              <th style={S.tableHeaderCellCenter}>Tjänat</th>
              <th style={S.tableHeaderCellCenter}>Överförda</th>
              <th style={S.tableHeaderCellCenter}>Sparat</th>
              <th style={S.tableHeaderCellCenter}>Använd</th>
              <th style={S.tableHeaderCellCenter}>Kvar</th>
              <th style={S.tableHeaderCellCenter}>Förskott</th>
              <th style={S.tableHeaderCell}></th>
            </tr>
          </thead>
          <tbody>
            {employees.map(emp => {
              const empId = emp.employee_id;
              const snap = snapshots[empId] || {};
              const draft = drafts[empId] || { earnedDays: '0', carriedOverDays: '0', savedDays: '0' };
              const isDirty = (
                Number(draft.earnedDays) !== Number(snap.earnedDays || 0)
                || Number(draft.carriedOverDays) !== Number(snap.carriedOverDays || 0)
                || Number(draft.savedDays) !== Number(snap.savedDays || 0)
              );
              const hasAdvance = (snap.advanceDaysTaken || 0) > 0;
              return (
                <tr key={empId}>
                  <td style={{ ...S.tableCell, fontWeight: 600 }}>
                    {emp.firstname} {emp.lastname}
                  </td>
                  <td style={S.tableCellCenter}>
                    <input
                      type="number" min="0" step="0.5"
                      value={draft.earnedDays}
                      onChange={e => setDraftField(empId, 'earnedDays', e.target.value)}
                      aria-label={`Tjänade semesterdagar för ${emp.firstname}`}
                      style={{ width: 70, padding: '4px 6px', border: `1px solid ${S.colors.borderDefault}`, borderRadius: 4, textAlign: 'right' }}
                    />
                  </td>
                  <td style={S.tableCellCenter}>
                    <input
                      type="number" min="0" step="0.5"
                      value={draft.carriedOverDays}
                      onChange={e => setDraftField(empId, 'carriedOverDays', e.target.value)}
                      aria-label={`Överförda semesterdagar för ${emp.firstname}`}
                      style={{ width: 70, padding: '4px 6px', border: `1px solid ${S.colors.borderDefault}`, borderRadius: 4, textAlign: 'right' }}
                    />
                  </td>
                  <td style={S.tableCellCenter}>
                    <input
                      type="number" min="0" step="0.5"
                      value={draft.savedDays}
                      onChange={e => setDraftField(empId, 'savedDays', e.target.value)}
                      aria-label={`Sparade semesterdagar för ${emp.firstname}`}
                      style={{ width: 70, padding: '4px 6px', border: `1px solid ${S.colors.borderDefault}`, borderRadius: 4, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ ...S.tableCellCenter, color: S.colors.textMuted }}>
                    {snap.usedDays ?? '–'}
                  </td>
                  <td style={{ ...S.tableCellCenter, fontWeight: 600, color: (snap.remainingPaidDays || 0) > 0 ? S.colors.success : S.colors.textMuted }}>
                    {snap.remainingPaidDays ?? '–'}
                  </td>
                  <td style={{ ...S.tableCellCenter, color: hasAdvance ? '#b45309' : S.colors.textDisabled, fontWeight: hasAdvance ? 600 : 400 }}>
                    {snap.advanceDaysTaken ?? '–'}
                  </td>
                  <td style={S.tableCellCenter}>
                    <button
                      onClick={() => handleSave(empId)}
                      disabled={!isDirty || saving[empId]}
                      style={{
                        ...S.btnPrimary,
                        padding: '6px 12px',
                        fontSize: 12,
                        opacity: isDirty ? 1 : 0.4,
                      }}
                    >
                      <Save size={12} /> Spara
                    </button>
                  </td>
                </tr>
              );
            })}
            {employees.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: S.colors.textMuted }}>
                  Inga anställda hittades.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default VacationBalanceSection;
